import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  CheckCircle2,
  ArrowRight,
  Loader2,
  Users,
  Heart,
  Mail,
  Sparkles,
  Building2,
} from "lucide-react";

const STEPS = [
  { key: "welcome", label: "Welcome" },
  { key: "invite", label: "Invite caregiver" },
  { key: "client", label: "Add client" },
  { key: "family", label: "Share with family" },
];

export default function OnboardingWizard({ onClose, agency }) {
  const [stepIdx, setStepIdx] = useState(0);
  const [invite, setInvite] = useState(null);
  const [client, setClient] = useState(null);
  const [familyLink, setFamilyLink] = useState("");

  const next = () => setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));
  const back = () => setStepIdx((i) => Math.max(i - 1, 0));

  const finish = async () => {
    try {
      await api.put("/agencies/me", { onboarding_completed: true });
    } catch {
      /* non-fatal */
    }
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4"
      data-testid="onboarding-wizard"
    >
      <div className="bg-white sm:rounded-2xl w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-clinical-border px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-clinical-primary" />
              <span className="label-eyebrow !text-clinical-primary">Get started</span>
            </div>
            <button
              type="button"
              onClick={finish}
              className="text-xs text-clinical-subtext hover:text-clinical-text"
              data-testid="onboarding-skip"
            >
              Skip for now
            </button>
          </div>
          {/* Stepper */}
          <div className="mt-3 flex items-center gap-1.5">
            {STEPS.map((s, i) => (
              <div key={s.key} className="flex-1">
                <div
                  className={`h-1 rounded-full transition-colors ${
                    i <= stepIdx ? "bg-clinical-primary" : "bg-clinical-border"
                  }`}
                  data-testid={`onboarding-step-bar-${i}`}
                />
                <div className={`mt-1.5 text-[10px] uppercase tracking-wider ${
                  i === stepIdx ? "text-clinical-primary font-medium" : "text-clinical-subtext"
                }`}>
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="px-6 py-6 min-h-[300px]">
          {stepIdx === 0 && <Welcome agency={agency} onNext={next} />}
          {stepIdx === 1 && <InviteStep invite={invite} setInvite={setInvite} onNext={next} onBack={back} />}
          {stepIdx === 2 && <ClientStep client={client} setClient={setClient} onNext={next} onBack={back} />}
          {stepIdx === 3 && <FamilyStep client={client} familyLink={familyLink} setFamilyLink={setFamilyLink} onBack={back} onFinish={finish} />}
        </div>
      </div>
    </div>
  );
}

function Welcome({ agency, onNext }) {
  return (
    <div data-testid="onboarding-step-welcome">
      <Building2 className="h-10 w-10 text-clinical-primary mb-3" />
      <h2 className="font-heading text-2xl font-semibold text-clinical-text">
        Welcome to Afyanex, {agency?.name || "your agency"} 👋
      </h2>
      <p className="mt-2 text-sm text-clinical-subtext">
        In the next ~5 minutes we'll get you to a live family invite. Three quick steps:
      </p>
      <ul className="mt-4 space-y-2 text-sm">
        <li className="flex items-start gap-2"><Users className="h-4 w-4 text-clinical-primary mt-0.5" /> Invite your first caregiver</li>
        <li className="flex items-start gap-2"><Heart className="h-4 w-4 text-clinical-primary mt-0.5" /> Add your first client</li>
        <li className="flex items-start gap-2"><Mail className="h-4 w-4 text-clinical-primary mt-0.5" /> Generate a private family-portal link</li>
      </ul>
      <button
        type="button"
        onClick={onNext}
        className="btn-primary mt-6"
        data-testid="onboarding-welcome-next"
      >
        Let's go <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );
}

function InviteStep({ invite, setInvite, onNext, onBack }) {
  const [form, setForm] = useState({ email: "", name: "", role: "caregiver" });
  const [saving, setSaving] = useState(false);

  const send = async () => {
    if (!form.email) {
      toast.error("Email is required");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/invites", { ...form, ttl_days: 10 });
      setInvite(data);
      toast.success("Invite created. Copy the link to send.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create invite");
    } finally {
      setSaving(false);
    }
  };

  const link = invite ? `${window.location.origin}/invite/${invite.token}` : "";

  return (
    <div data-testid="onboarding-step-invite">
      <Users className="h-10 w-10 text-clinical-primary mb-3" />
      <h2 className="font-heading text-xl font-semibold text-clinical-text">
        Invite your first caregiver
      </h2>
      <p className="mt-1 text-sm text-clinical-subtext">
        They'll get a private link to set their own password and acknowledge the HIPAA notice.
      </p>

      {!invite ? (
        <div className="mt-4 space-y-3">
          <div>
            <label className="label-eyebrow mb-1.5 block">Caregiver email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="input-clinical"
              data-testid="onboarding-invite-email"
            />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Name (optional)</label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="input-clinical"
              data-testid="onboarding-invite-name"
            />
          </div>
          <button
            type="button"
            onClick={send}
            disabled={saving}
            className="btn-primary"
            data-testid="onboarding-invite-create"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate invite link"}
          </button>
        </div>
      ) : (
        <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
          <div className="flex items-center gap-2 text-emerald-800">
            <CheckCircle2 className="h-4 w-4" />
            <span className="font-medium text-sm">Invite ready — copy this link</span>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <input
              readOnly
              value={link}
              className="input-clinical font-mono text-xs"
              data-testid="onboarding-invite-link"
              onFocus={(e) => e.target.select()}
            />
            <button
              type="button"
              onClick={() => {
                navigator.clipboard.writeText(link);
                toast.success("Copied to clipboard");
              }}
              className="btn-secondary"
              data-testid="onboarding-invite-copy"
            >
              Copy
            </button>
          </div>
          <p className="text-[11px] text-emerald-700 mt-2">
            Send by SMS/email. Expires in 10 days.
          </p>
        </div>
      )}

      <div className="mt-6 flex items-center justify-between">
        <button type="button" onClick={onBack} className="btn-ghost" data-testid="onboarding-invite-back">
          Back
        </button>
        <button
          type="button"
          onClick={onNext}
          className="btn-primary"
          data-testid="onboarding-invite-next"
        >
          {invite ? "Next: add a client" : "Skip for now"}
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function ClientStep({ client, setClient, onNext, onBack }) {
  const [form, setForm] = useState({ name: "", address: "", dob: "" });
  const [saving, setSaving] = useState(false);

  const add = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/clients", form);
      setClient(data);
      toast.success(`Client ${data.name} added`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add client");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="onboarding-step-client">
      <Heart className="h-10 w-10 text-clinical-primary mb-3" />
      <h2 className="font-heading text-xl font-semibold text-clinical-text">
        Add your first client
      </h2>
      <p className="mt-1 text-sm text-clinical-subtext">
        Just a name to get started — you can fill in medications, doctor info, and family contacts later.
      </p>

      {!client ? (
        <div className="mt-4 space-y-3">
          <div>
            <label className="label-eyebrow mb-1.5 block">Client name</label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="input-clinical"
              data-testid="onboarding-client-name"
              placeholder="e.g. Margaret Wilson"
            />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Address (optional)</label>
            <input
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              className="input-clinical"
              data-testid="onboarding-client-address"
            />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Date of birth (optional)</label>
            <input
              type="date"
              value={form.dob}
              onChange={(e) => setForm({ ...form, dob: e.target.value })}
              className="input-clinical"
              data-testid="onboarding-client-dob"
            />
          </div>
          <button
            type="button"
            onClick={add}
            disabled={saving}
            className="btn-primary"
            data-testid="onboarding-client-create"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add client"}
          </button>
        </div>
      ) : (
        <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-emerald-800" />
          <span className="text-sm text-emerald-800">
            Client <strong>{client.name}</strong> added — find them in Clients.
          </span>
        </div>
      )}

      <div className="mt-6 flex items-center justify-between">
        <button type="button" onClick={onBack} className="btn-ghost" data-testid="onboarding-client-back">
          Back
        </button>
        <button
          type="button"
          onClick={onNext}
          className="btn-primary"
          data-testid="onboarding-client-next"
        >
          {client ? "Next: family portal" : "Skip"}
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function FamilyStep({ client, familyLink, setFamilyLink, onBack, onFinish }) {
  const [recipient, setRecipient] = useState("");
  const [relationship, setRelationship] = useState("Daughter");
  const [saving, setSaving] = useState(false);

  const generate = async () => {
    if (!client) {
      toast.error("Go back and add a client first");
      return;
    }
    if (!recipient.trim()) {
      toast.error("Recipient name is required");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/clients/${client.id}/family-invites`, {
        recipient_name: recipient,
        relationship,
        ttl_days: 30,
      });
      const link = `${window.location.origin}/family/${data.token}`;
      setFamilyLink(link);
      toast.success("Family link ready");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create family link");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="onboarding-step-family">
      <Mail className="h-10 w-10 text-clinical-primary mb-3" />
      <h2 className="font-heading text-xl font-semibold text-clinical-text">
        Give the family a peek
      </h2>
      <p className="mt-1 text-sm text-clinical-subtext">
        Generate a private read-only link relatives can use to see formatted shift summaries.
        They never see raw notes or caregiver IDs.
      </p>

      {!client ? (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          Add a client in the previous step first.
        </div>
      ) : !familyLink ? (
        <div className="mt-4 space-y-3">
          <div>
            <label className="label-eyebrow mb-1.5 block">Recipient name</label>
            <input
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              className="input-clinical"
              placeholder={`e.g. ${(client.name || "").split(" ").pop()}'s daughter`}
              data-testid="onboarding-family-recipient"
            />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Relationship</label>
            <select
              value={relationship}
              onChange={(e) => setRelationship(e.target.value)}
              className="input-clinical"
              data-testid="onboarding-family-relationship"
            >
              {["Daughter", "Son", "Spouse", "Sibling", "Parent", "Grandchild", "Other"].map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={generate}
            disabled={saving}
            className="btn-primary"
            data-testid="onboarding-family-generate"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate family link"}
          </button>
        </div>
      ) : (
        <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
          <div className="flex items-center gap-2 text-emerald-800">
            <CheckCircle2 className="h-4 w-4" />
            <span className="font-medium text-sm">All set — your first family link is live!</span>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <input
              readOnly
              value={familyLink}
              className="input-clinical font-mono text-xs"
              data-testid="onboarding-family-link"
              onFocus={(e) => e.target.select()}
            />
            <button
              type="button"
              onClick={() => {
                navigator.clipboard.writeText(familyLink);
                toast.success("Copied to clipboard");
              }}
              className="btn-secondary"
              data-testid="onboarding-family-copy"
            >
              Copy
            </button>
          </div>
        </div>
      )}

      <div className="mt-6 flex items-center justify-between">
        <button type="button" onClick={onBack} className="btn-ghost" data-testid="onboarding-family-back">
          Back
        </button>
        <button
          type="button"
          onClick={onFinish}
          className="btn-primary"
          data-testid="onboarding-finish"
        >
          Done — open my dashboard <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
